import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Edit, Share2, ShoppingBag, User, Phone, Mail } from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import Card from '../../components/common/Card';
import Accordion from '../../components/common/Accordion';

/**
 * GuidePage - 사용설명서 페이지
 */
const GuidePage = () => {
  const navigate = useNavigate();
  const guideSteps = [
    {
      icon: Edit,
      title: '부고 작성하기',
      description: '모바일에서 3단계로 간편하게 부고를 작성할 수 있습니다.',
      steps: [
        '상단 메뉴에서 "부고 작성" 버튼을 클릭합니다.',
        '[1단계] 고인의 정보를 입력합니다. (이름, 생년월일, 관계 등)',
        '[2단계] 장례 정보를 입력합니다. (장례식장, 주소, 발인일시 등)',
        '[3단계] 상주 정보를 입력하고 미리보기로 확인한 후 작성 완료합니다.',
        'PC에서는 한 페이지에서 모든 정보를 한번에 입력할 수 있습니다.'
      ]
    },
    {
      icon: Share2,
      title: '부고 공유하기',
      description: '작성한 부고를 카카오톡, 문자, 링크 등으로 공유할 수 있습니다.',
      steps: [
        '부고 상세 페이지에서 "공유하기" 버튼을 클릭합니다.',
        '카카오톡, 문자, 링크 복사 중 원하는 방법을 선택합니다.',
        '카카오톡: 카카오톡 앱이 열리며 대화방을 선택하여 전송합니다.',
        '문자: 문자 앱이 열리며 수신자를 선택하여 전송합니다.',
        '링크 복사: 부고 링크가 클립보드에 복사되어 원하는 곳에 붙여넣을 수 있습니다.'
      ]
    },
    {
      icon: ShoppingBag,
      title: '화환 주문하기',
      description: '온라인으로 간편하게 화환을 주문하고 배송받을 수 있습니다.',
      steps: [
        '"화환 보내기" 메뉴에서 원하는 화환 상품을 선택합니다.',
        '상품 상세 페이지에서 "장바구니 담기"를 클릭합니다.',
        '장바구니에서 수량을 조절하고 "주문하기"를 클릭합니다.',
        '배송지 정보와 결제 수단을 선택합니다.',
        '주문을 완료하면 주문 번호가 발급되며, 마이페이지에서 배송 상태를 확인할 수 있습니다.',
        '발인 전날 오후 6시까지 주문하시면 당일 배송이 가능합니다.'
      ]
    },
    {
      icon: User,
      title: '회원가입 및 로그인',
      description: '회원가입 후 부고 작성, 화환 주문 등의 서비스를 이용할 수 있습니다.',
      steps: [
        '상단 메뉴에서 "로그인" 버튼을 클릭합니다.',
        '카카오 또는 네이버 계정으로 간편 로그인하거나, 이메일로 회원가입합니다.',
        '회원가입 시 이름, 전화번호, 이메일을 입력합니다.',
        '로그인 후 마이페이지에서 회원정보를 수정할 수 있습니다.',
        '비회원은 부고 조회와 화환 상품 조회만 가능합니다.'
      ]
    }
  ];

  const tipsAndTricks = [
    {
      title: '부고 작성 팁',
      content: '부고 작성 시 장례식장 주소는 정확히 입력해주세요. 지도 검색 기능을 활용하면 더욱 편리합니다. 상주 정보는 최대 3명까지 입력 가능하며, 연락처는 필수 입력 사항입니다.'
    },
    {
      title: '화환 주문 시 유의사항',
      content: '화환은 발인 전날 오후 6시까지 주문하시면 당일 배송이 가능합니다. 급하신 경우 고객센터로 연락주시면 빠른 배송을 도와드립니다. 조화와 생화 중 선택 가능하며, 메시지 카드를 함께 보낼 수 있습니다.'
    },
    {
      title: '부고 수정 및 삭제',
      content: '마이페이지의 "내 부고 관리"에서 작성하신 부고를 수정하거나 삭제할 수 있습니다. 이미 공유된 부고를 수정하시면 기존 링크로 접속한 사용자들도 업데이트된 내용을 보게 됩니다.'
    },
    {
      title: '답례 인사 작성',
      content: '장례가 끝난 후 "답례 인사" 메뉴에서 감사의 마음을 담은 인사말을 작성할 수 있습니다. 작성하신 내용은 부고 페이지에 자동으로 게시되어 방문하신 분들이 확인할 수 있습니다.'
    },
    {
      title: '즐겨찾기 기능',
      content: '자주 확인하는 부고는 즐겨찾기에 추가할 수 있습니다. 부고 상세 페이지에서 하트 아이콘을 클릭하면 즐겨찾기에 추가되며, 마이페이지에서 모아볼 수 있습니다.'
    }
  ];

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* 헤더 */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <BookOpen size={32} className="text-primary-600" />
          </div>
          <h1 className="text-2xl font-bold text-primary-900 mb-2">사용설명서</h1>
          <p className="text-sm text-accent-600">
            서비스 이용 방법을 단계별로 안내해드립니다
          </p>
        </div>

        {/* 목차 (앵커 네비게이션) */}
        <nav
          aria-label="가이드 목차"
          className="mb-8 rounded-lg border border-accent-200 bg-secondary-50 p-4"
        >
          <h2 className="mb-2 text-sm font-semibold text-primary-900">목차</h2>
          <ul className="flex flex-wrap gap-2">
            {guideSteps.map((guide, index) => (
              <li key={index}>
                <a
                  href={`#guide-${index}`}
                  className="inline-block rounded-md bg-white px-3 py-1 text-sm text-primary-700 hover:bg-primary-50 hover:text-primary-900"
                >
                  {guide.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* 주요 기능 가이드 */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-primary-900 mb-4">주요 기능 가이드</h2>
          <div className="space-y-4">
            {guideSteps.map((guide, index) => {
              const IconComponent = guide.icon;
              return (
                <div key={index} id={`guide-${index}`} className="scroll-mt-24">
                <Card>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                      <IconComponent size={24} className="text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-primary-900 mb-1">
                        {guide.title}
                      </h3>
                      <p className="text-sm text-accent-600 mb-3">
                        {guide.description}
                      </p>
                      <ol className="space-y-2">
                        {guide.steps.map((step, stepIndex) => (
                          <li key={stepIndex} className="text-sm text-primary flex">
                            <span className="font-semibold text-primary-600 mr-2">
                              {stepIndex + 1}.
                            </span>
                            <span>{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </Card>
                </div>
              );
            })}
          </div>
        </div>

        {/* 알아두면 좋은 팁 */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-primary-900 mb-4">알아두면 좋은 팁</h2>
          <div className="space-y-3">
            {tipsAndTricks.map((tip, index) => (
              <Accordion key={index} title={tip.title}>
                <p className="text-sm text-primary leading-relaxed">
                  {tip.content}
                </p>
              </Accordion>
            ))}
          </div>
        </div>

        {/* 추가 도움말 */}
        <Card className="bg-secondary-50 border-accent-200">
          <h3 className="font-semibold text-primary-900 mb-2">추가 문의가 필요하신가요?</h3>
          <p className="text-sm text-accent-600 mb-3">
            사용설명서로 해결되지 않은 문제가 있으시면 고객센터나 1:1 문의를 이용해주세요.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/support/faq')}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 underline"
            >
              FAQ 보기 →
            </button>
            <span className="text-accent-400">|</span>
            <button
              onClick={() => navigate('/support/inquiry')}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 underline"
            >
              1:1 문의하기 →
            </button>
          </div>
        </Card>

        {/* 고객센터 정보 */}
        <div className="mt-6 text-center">
          <p className="text-sm text-accent-600 mb-2">고객센터</p>
          <a
            href="tel:1588-0000"
            className="inline-flex items-center gap-1.5 text-lg font-semibold text-primary-900 hover:text-primary-700"
          >
            <Phone size={18} />
            1588-0000
          </a>
          <p className="text-xs text-accent-500 mt-1">평일 09:00 - 18:00</p>
          <a
            href="mailto:support@funeral-service.com"
            className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700"
          >
            <Mail size={14} />
            support@funeral-service.com
          </a>
        </div>
      </div>
    </MainLayout>
  );
};

export default GuidePage;
